import type { SavvyFormUserDataReadResult } from './sentryObservability'

//* Shared cookie name prefix for experiment splits (`Embeddables.<flowId>`).
//* Keep in sync with the write site in renderer_eval_js_v3 and parseUserDataFromCookies.
export const EXPERIMENT_COOKIE_PREFIX = 'Embeddables.'

export function getExperimentCookieName({ flowId }: { flowId: string }): string {
  return `${EXPERIMENT_COOKIE_PREFIX}${flowId}`
}

function isValidJsonString(str: string): boolean {
  try {
    !str || JSON.parse(str)
    return true
  } catch {
    return false
  }
}

//* Same guard as parseUserDataFromCookies: skip empty values so probe and recovery
//* never disagree on edge-case cookie shapes (`key=`). Split only on the first `=` so
//* raw JSON values from the eval writer (which may contain `=`) are preserved.
function parseDocumentCookies(cookieHeader: string): { [key: string]: string } {
  const cookies: { [key: string]: string } = {}
  cookieHeader.split(';').forEach((cookie: string) => {
    const trimmed = cookie.trim()
    const eq = trimmed.indexOf('=')
    if (eq === -1) return
    const key = trimmed.slice(0, eq)
    const value = trimmed.slice(eq + 1)
    if (key && value) {
      cookies[key] = value
    }
  })
  return cookies
}

//* Encoded cookies need decodeURIComponent; legacy un-encoded JSON may contain `%` sequences
//* that throw. Fall back to the raw value so valid legacy cookies still parse.
function decodeCookieValue(rawValue: string): string {
  try {
    return decodeURIComponent(rawValue)
  } catch {
    return rawValue
  }
}

//* Read document.cookie for Embeddables.<flowId>. Decoding mirrors parseUserDataFromCookies
//* in api/handleRequest.ts (decodeURIComponent + JSON + keep only split_* keys).
export function readExperimentSplitsFromCookie({
  flowId,
}: {
  flowId: string
}): { [key: string]: string } | null {
  let cookieHeader: string
  try {
    cookieHeader = document.cookie
  } catch {
    return null
  }

  if (!cookieHeader) return null

  const cookies = parseDocumentCookies(cookieHeader)
  const cookieKey = getExperimentCookieName({ flowId })
  const userDataCookie = cookies[cookieKey]
  if (!userDataCookie) return null

  try {
    const decoded = decodeCookieValue(userDataCookie)
    if (!isValidJsonString(decoded)) {
      return null
    }

    const userData = JSON.parse(decoded) as Record<string, unknown>
    const filteredData: { [key: string]: string } = {}
    Object.keys(userData).forEach((key) => {
      if (key.startsWith('split_')) {
        filteredData[key] = String(userData[key])
      }
    })

    return Object.keys(filteredData).length > 0 ? filteredData : null
  } catch {
    return null
  }
}

export type ExperimentCookieInspection = {
  experiment_cookie_present: boolean
  experiment_cookie_split_key_count: number
  experiment_cookie_parse_ok: boolean
}

//* Presence / parse probe for load_user_data_before. Distinguishes "cookie missing",
//* "cookie present but unparseable", and "cookie holds N split_* keys".
export function inspectExperimentCookie({ flowId }: { flowId: string }): ExperimentCookieInspection {
  let cookieHeader: string
  try {
    cookieHeader = document.cookie
  } catch {
    return {
      experiment_cookie_present: false,
      experiment_cookie_split_key_count: 0,
      experiment_cookie_parse_ok: false,
    }
  }

  const cookies = cookieHeader ? parseDocumentCookies(cookieHeader) : {}
  const cookieKey = getExperimentCookieName({ flowId })
  const rawValue = cookies[cookieKey]
  if (!rawValue) {
    return {
      experiment_cookie_present: false,
      experiment_cookie_split_key_count: 0,
      experiment_cookie_parse_ok: false,
    }
  }

  try {
    const decoded = decodeCookieValue(rawValue)
    if (!isValidJsonString(decoded)) {
      return {
        experiment_cookie_present: true,
        experiment_cookie_split_key_count: 0,
        experiment_cookie_parse_ok: false,
      }
    }

    const userData = JSON.parse(decoded) as Record<string, unknown>
    const splitKeyCount = Object.keys(userData).filter((key) => key.startsWith('split_')).length

    return {
      experiment_cookie_present: true,
      experiment_cookie_split_key_count: splitKeyCount,
      experiment_cookie_parse_ok: true,
    }
  } catch {
    return {
      experiment_cookie_present: true,
      experiment_cookie_split_key_count: 0,
      experiment_cookie_parse_ok: false,
    }
  }
}

export type ExperimentSplitSource =
  | 'local_storage'
  | 'cookie_recovered'
  | 'cookie_write_failed'
  | 'window_recovered'
  | 'window_write_failed'
  | 'none'

const RECOVERABLE_STORAGE_READ_RESULTS: ReadonlySet<SavvyFormUserDataReadResult> = new Set([
  'null',
  'empty',
  'missing_flow',
])

export function shouldAttemptExperimentCookieRecovery({
  storageReadResult,
  storeExperimentsInCookies,
  doNotStoreAnyData,
}: {
  storageReadResult: SavvyFormUserDataReadResult
  storeExperimentsInCookies: boolean | undefined
  doNotStoreAnyData: boolean | undefined
}): boolean {
  return (
    RECOVERABLE_STORAGE_READ_RESULTS.has(storageReadResult) &&
    !!storeExperimentsInCookies &&
    !doNotStoreAnyData
  )
}

//* Merge recovered split_* keys into the flow's SavvyFormUserData slice and write it back so
//* loadUserData / writeUserData / split_persist_check keep the healthy-session path.
export function writeRecoveredExperimentSplitsToStorage({
  flowId,
  splits,
}: {
  flowId: string
  splits: { [key: string]: string }
}): boolean {
  try {
    let allData: Record<string, Record<string, unknown>> = {}
    const raw = localStorage.getItem('SavvyFormUserData')
    if (raw && raw !== '{}') {
      try {
        const parsed = JSON.parse(raw) as Record<string, Record<string, unknown>>
        //* Arrays pass typeof === 'object'; assigning into them then JSON.stringify drops
        //* non-index keys, so treat array roots as empty and rewrite a proper object map.
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          allData = parsed
        }
      } catch {
        allData = {}
      }
    }

    const existingFlowData =
      allData[flowId] && typeof allData[flowId] === 'object' && !Array.isArray(allData[flowId])
        ? allData[flowId]
        : {}
    allData[flowId] = { ...existingFlowData, ...splits }
    localStorage.setItem('SavvyFormUserData', JSON.stringify(allData))
    return true
  } catch {
    return false
  }
}

export function recoverExperimentSplitsFromCookie({
  flowId,
  storageReadResult,
  storeExperimentsInCookies,
  doNotStoreAnyData,
}: {
  flowId: string
  storageReadResult: SavvyFormUserDataReadResult
  storeExperimentsInCookies: boolean | undefined
  doNotStoreAnyData: boolean | undefined
}): {
  splitSource: ExperimentSplitSource
  recoveredSplitKeyCount: number
} {
  if (storageReadResult === 'ok') {
    return { splitSource: 'local_storage', recoveredSplitKeyCount: 0 }
  }

  if (
    !shouldAttemptExperimentCookieRecovery({
      storageReadResult,
      storeExperimentsInCookies,
      doNotStoreAnyData,
    })
  ) {
    return { splitSource: 'none', recoveredSplitKeyCount: 0 }
  }

  const splits = readExperimentSplitsFromCookie({ flowId })
  if (!splits) {
    return { splitSource: 'none', recoveredSplitKeyCount: 0 }
  }

  const wrote = writeRecoveredExperimentSplitsToStorage({ flowId, splits })
  if (!wrote) {
    //* Cookie was valid but storage repair failed — keep this distinct from "no cookie"
    //* so Sentry can tell recovery attempted vs cookie missing.
    return { splitSource: 'cookie_write_failed', recoveredSplitKeyCount: 0 }
  }

  return {
    splitSource: 'cookie_recovered',
    recoveredSplitKeyCount: Object.keys(splits).length,
  }
}

//* Window-variable fallback for experiment splits when the primary SavvyFormUserData read at
//* connect() is empty. Population point is renderer_eval_js_v3.ts, the only place that already
//* holds authoritative split_* values before the bundle boots. renderer_eval_js_v3.ts imports
//* this constant to write the same key, so the eval string and the typed read side cannot drift.
export const EXPERIMENT_WINDOW_FALLBACK_KEY = '__EMBEDDABLES_EXPERIMENT_SPLITS_FALLBACK__'

declare global {
  interface Window {
    //* Keyed by flow id so multi-embed pages do not overwrite each other's fallback data.
    //* Same-document only (does not cross iframe/SSR-host boundaries).
    [EXPERIMENT_WINDOW_FALLBACK_KEY]?: Record<string, { [key: string]: string }>
  }
}

//* Read the split_* subset published on the window by the eval script for a flow. Returns
//* null (not `{}`) when absent so callers can tell "no fallback data" from "empty object".
export function readExperimentSplitsFromWindow({
  flowId,
}: {
  flowId: string
}): { [key: string]: string } | null {
  try {
    const splits = window[EXPERIMENT_WINDOW_FALLBACK_KEY]?.[flowId]
    if (!splits || Object.keys(splits).length === 0) return null
    return splits
  } catch {
    return null
  }
}

//* Mirrors shouldAttemptExperimentCookieRecovery, minus the store_experiments_in_cookies gate:
//* the window fallback is unconditional (not opt-in per flow) other than the privacy gate that
//* also suppresses the eval write it depends on.
export function shouldAttemptWindowSplitRecovery({
  storageReadResult,
  doNotStoreAnyData,
}: {
  storageReadResult: SavvyFormUserDataReadResult
  doNotStoreAnyData: boolean | undefined
}): boolean {
  return RECOVERABLE_STORAGE_READ_RESULTS.has(storageReadResult) && !doNotStoreAnyData
}

//* Recover split_* keys from the window fallback into SavvyFormUserData when the primary read
//* was empty. Shares its return shape with recoverExperimentSplitsFromCookie so flow_controller
//* can log a single split_source regardless of which fallback lane recovered the session.
export function recoverExperimentSplitsFromWindow({
  flowId,
  storageReadResult,
  doNotStoreAnyData,
}: {
  flowId: string
  storageReadResult: SavvyFormUserDataReadResult
  doNotStoreAnyData: boolean | undefined
}): {
  splitSource: ExperimentSplitSource
  recoveredSplitKeyCount: number
} {
  if (storageReadResult === 'ok') {
    return { splitSource: 'local_storage', recoveredSplitKeyCount: 0 }
  }

  if (!shouldAttemptWindowSplitRecovery({ storageReadResult, doNotStoreAnyData })) {
    return { splitSource: 'none', recoveredSplitKeyCount: 0 }
  }

  const splits = readExperimentSplitsFromWindow({ flowId })
  if (!splits) {
    return { splitSource: 'none', recoveredSplitKeyCount: 0 }
  }

  const wrote = writeRecoveredExperimentSplitsToStorage({ flowId, splits })
  if (!wrote) {
    //* Window data was valid but the storage repair itself failed — keep distinct from
    //* "no fallback data" so Sentry can tell recovery attempted vs unavailable.
    return { splitSource: 'window_write_failed', recoveredSplitKeyCount: 0 }
  }

  return {
    splitSource: 'window_recovered',
    recoveredSplitKeyCount: Object.keys(splits).length,
  }
}

export type WindowFallbackInspection = {
  window_fallback_present: boolean
  window_fallback_split_key_count: number
}

//* Presence/count probe for load_user_data_before, mirroring inspectExperimentCookie so both
//* fallback lanes are equally measurable regardless of which one ends up recovering.
export function inspectWindowFallback({ flowId }: { flowId: string }): WindowFallbackInspection {
  const splits = readExperimentSplitsFromWindow({ flowId })
  if (!splits) {
    return { window_fallback_present: false, window_fallback_split_key_count: 0 }
  }
  return {
    window_fallback_present: true,
    window_fallback_split_key_count: Object.keys(splits).length,
  }
}
